import React from "react";
import PropTypes from "prop-types";
import {
	Section,
	SectionTitleAlign,
	SectionTitleSize,
	SectionTheme,
	SectionPadding,
	SectionPaddingControl,
} from "../../components/Section";
import { CloudinaryImage } from "../../components/CloudinaryImage";

const SectionConnector = ({ data, useWrapper, children }) => {
	let titleSize;
	switch (data.headingSize) {
		case "Extra Large":
			titleSize = SectionTitleSize.xl;
			break;
		case "Large":
			titleSize = SectionTitleSize.lg;
			break;
		case "Medium":
			titleSize = SectionTitleSize.md;
			break;
		case "Small":
			titleSize = SectionTitleSize.sm;
			break;
		case "Extra Small":
			titleSize = SectionTitleSize.xs;
			break;
		case "Micro":
			titleSize = SectionTitleSize.micro;
			break;
		case "Eyebrow":
			titleSize = SectionTitleSize.eyebrow;
			break;
		default:
			titleSize = SectionTitleSize.xl;
			break;
	}

	let titleAlignment;
	switch (data.titleAlignment) {
		case "Center":
			titleAlignment = SectionTitleAlign.center;
			break;
		case "Left":
		default:
			titleAlignment = SectionTitleAlign.left;
			break;
	}

	let padding;
	switch (data.padding) {
		case "None":
			padding = SectionPadding.none;
			break;
		case "Micro":
			padding = SectionPadding.micro;
			break;
		case "Extra Small":
			padding = SectionPadding.extraSmall;
			break;
		case "Small":
			padding = SectionPadding.small;
			break;
		case "Large":
			padding = SectionPadding.large;
			break;
		case "Extra Large":
			padding = SectionPadding.extraLarge;
			break;
		case "Medium":
		default:
			padding = SectionPadding.medium;
			break;
	}

	let paddingControl;
	switch (data.paddingControl) {
		case "Remove Top":
			paddingControl = SectionPaddingControl.removeTop;
			break;
		case "Remove Bottom":
			paddingControl = SectionPaddingControl.removeBottom;
			break;
		default:
			break;
	}

	let theme;
	switch (data.theme) {
		case "Dark":
			theme = SectionTheme.dark;
			break;
		case "Light":
			theme = SectionTheme.light;
			break;
		case "Transparent":
		default:
			theme = SectionTheme.transparent;
			break;
	}

	return useWrapper ? (
		<Section
			key={data.id}
			title={data.heading}
			titleAs={data.headingAs || "h2"}
			titleSize={titleSize}
			titleAlignment={titleAlignment}
			titleId={data.titleId}
			padding={padding}
			paddingControl={paddingControl}
			theme={theme}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...(data.backgroundImage?.[0] && {
				backgroundImage: (
					<CloudinaryImage
						cloudinary={{
							...data.backgroundImage[0],
							aspectRatio: data.backgroundImage[0].height / data.backgroundImage[0].width,
						}}
					/>
				),
			})}>
			{children}
		</Section>
	) : (
		// TODO : 🤢 - Fix grid system to remove span
		<span>{children}</span>
	);
};

SectionConnector.propTypes = {
	data: PropTypes.shape({
		id: PropTypes.string,
		heading: PropTypes.string,
		headingAs: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "p"]),
		headingSize: PropTypes.oneOf(["Extra Large", "Large", "Medium", "Small", "Micro", "Eyebrow"]),
		titleAlignment: PropTypes.oneOf(["Left", "Center"]),
		titleId: PropTypes.string,
		theme: PropTypes.oneOf(["Light", "Dark", "Transparent"]),
		padding: PropTypes.oneOf([
			"None",
			"Micro",
			"Extra Small",
			"Small",
			"Medium",
			"Large",
			"Extra Large",
		]),
		paddingControl: PropTypes.oneOf(["Remove Top", "Remove Bottom"]),
		backgroundImage: PropTypes.arrayOf(
			PropTypes.shape({
				secure_url: PropTypes.string,
				width: PropTypes.number,
				height: PropTypes.number,
				raw_transformation: PropTypes.string,
				version: PropTypes.number,
				public_id: PropTypes.string,
				format: PropTypes.string,
			})
		),
		wrapperSections: PropTypes.object,
	}).isRequired,
	children: PropTypes.node.isRequired,
	useWrapper: PropTypes.bool,
};

SectionConnector.defaultProps = {
	useWrapper: true,
};

export { SectionConnector };
